/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DoCheck, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {PDF_CREATION_METHOD, TABS} from '../common/constants';
import {catchError, EMPTY, of, Subscription, tap, timeout, TimeoutError} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {PlanModelService} from '../common/service/plan-model.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {
    DigitalPlanAttachmentCsdAttributesLookup,
    DigitalPlanAttachmentLotNumbersField,
    DigitalPlanAttachmentVestingLot,
    DigitalPlanAttachmentVirusScanStatusResponse,
    DigitalPlanValidationStatusResponseResult
} from './digital-plan.model';
import {LotDeclaration} from './lot-declaration';
import {DigitalPlanService} from './digital-plan.service';
import {DigitalPlanValidationState} from './digital-plan-validation-state';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {LookupService} from '../common/service/lookup.service';
import {PermissionsService} from '../common/service/permissions.service';
import {AlertModalService, ConfirmationModalService} from '../common/component/modal';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {NgSelectModule} from '@ng-select/ng-select';
import {VestingPurposeLookup} from '../common/models/vesting-purpose.model';
import {NgClass, NgStyle, NgTemplateOutlet} from '@angular/common';
import {UiConfigurationService} from '../common/service/ui-configuration.service';
import {TenureService} from '../plan-information/tenure/tenure.service';

@Component({
    selector: 'nlr-digital-plan',
    standalone: true,
    imports: [FormsModule, NgSelectModule, NgClass, NgStyle, NgTemplateOutlet],
    templateUrl: './digital-plan.component.html',
    styleUrl: './digital-plan.component.scss',
    // Provided per component instance so the validation state is scoped to (and destroyed with) this tab.
    providers: [DigitalPlanValidationState]
})
export class DigitalPlanComponent implements OnInit, DoCheck, OnDestroy {
    protected readonly title = TABS.DIGITAL_PLAN;

    private readonly permissions = ['EDIT_INFORMATION'];

    private static readonly UPLOAD_ERROR_MESSAGE =
        'The file failed to upload. Please try again and if the problem persists please contact Landgate Customer Services.';

    private static readonly UPLOAD_TIMEOUT_ERROR_MESSAGE = 'The upload is taking longer than expected. Please check your connection and try again.';

    private static readonly UPLOAD_FILE_INFECTED_ERROR_MESSAGE = 'The file is infected.';

    // Covers the whole upload, from reading the file through to its virus scan status, as that is how long the user waits
    private static readonly UPLOAD_TIMEOUT = 60 * 1000;

    // Max file size 10MB
    private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024;

    /**
     * The purposes each declaration is asked of, as the client set them out. Held as lists rather than derived from the plan,
     * because a digital plan carries no lots to read a common property lot or an acquisition transfer action off, and the
     * lookups behind those things describe what a surveyor may pick in the portal rather than what may be declared here.
     */
    private static readonly COMMON_PROPERTY_LOT_PURPOSES = [
        'SSTP_SUBD',
        'SSTP_RSUB_T4',
        'SSTP_ADD_CP_T1A',
        'SSTP_TFR_TO_CP_T1B',
        'SSTP_CONV_P3D3',
        'SSTP_RDEF_BDYS'
    ];

    private static readonly VESTING_LOT_PURPOSES = ['FH_SUBD', 'SSTP_SUBD', 'SSTP_RSUB_T4', 'STPL_SUBD', 'STPL_RSUB_T4'];

    /** Freehold or Crown, subdividing or acquiring: the purposes that can both acquire a lot and dedicate one as road. */
    private static readonly ACQUISITION_LOT_PURPOSES = ['FH_SUBD', 'CR_SUBD', 'FH_TAKE', 'CR_TAKE'];

    private static readonly CROWN_TRANSFER_LOT_PURPOSES = ['FH_SUBD'];

    @ViewChild('digitalPlanForm')
    form: NgForm;

    @ViewChild('dwgFileInput')
    dwgFileInput: ElementRef;

    dwgFile: any;

    validationClearedSubscription: Subscription;

    saveSubscription: Subscription;

    uploadDwgErrorMessage: string;

    uploadDwgSubscription: Subscription;

    regionOptions = [
        {name: 'Metro', value: true},
        {name: 'Rural', value: false}
    ];

    metro: boolean;

    meridianOptions = [
        {name: 'Yes', value: true},
        {name: 'No', value: false}
    ];

    yesNoOptions = [
        {name: 'Yes', value: true},
        {name: 'No', value: false}
    ];

    /**
     * The common property lots declaration. Named apart from the others because it alone feeds the CP prefix in the plan
     * heading and on the plan PDF.
     */
    commonPropertyDeclaration: LotDeclaration;

    /**
     * The declarations that stand in for the acquisition transfer actions a portal plan holds against its lots, in the order
     * the client asked for them.
     */
    acquisitionDeclarations: LotDeclaration[] = [];

    vestingPurposeLookups: VestingPurposeLookup[] = [];

    digitalPlanAttachmentCsdAttributesLookups: DigitalPlanAttachmentCsdAttributesLookup[] = [];

    digitalPlanAttachmentCsdAttributesLookup: DigitalPlanAttachmentCsdAttributesLookup;

    planLodgementIdAtLastCheck: number;

    constructor(
        private eventBus: NgEventBus,
        private http: HttpClient,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private digitalPlanService: DigitalPlanService,
        private lookupService: LookupService,
        private notificationAlertsService: NotificationAlertsService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private tenureService: TenureService,
        private uiConfigurationService: UiConfigurationService,
        // Shared with the hamburger (see DigitalPlanValidationState); provided per component so it is scoped to this tab.
        readonly validationState: DigitalPlanValidationState
    ) {}

    ngOnInit() {
        this.digitalPlanAttachmentCsdAttributesLookups = this.lookupService.getLookups('digitalPlanAttachmentCsdAttributes');
        this.vestingPurposeLookups = this.lookupService.getLookups('vestingPurpose');

        this.commonPropertyDeclaration = this.lotDeclaration(
            'commonPropertyLots',
            'Common property lots created on the plan?',
            'Common property lots',
            'Common property lot',
            () => this.purposeIsOneOf(DigitalPlanComponent.COMMON_PROPERTY_LOT_PURPOSES)
        );

        this.acquisitionDeclarations = [
            this.lotDeclaration(
                'lotsAcquiredForPublicPurpose',
                'Lots to be acquired for a public purpose?',
                'Lots to be acquired for a public purpose',
                'Lot',
                () => this.purposeIsOneOf(DigitalPlanComponent.ACQUISITION_LOT_PURPOSES)
            ),
            this.lotDeclaration(
                'lotsTransferredToCrown',
                'Lots to be transferred to the Crown?',
                'Lots to be transferred to the Crown',
                'Lot',
                () => this.purposeIsOneOf(DigitalPlanComponent.CROWN_TRANSFER_LOT_PURPOSES),
                () => this.crownTransferConflictMessage()
            ),
            this.lotDeclaration('roadLots', 'Any road (as a lot) created on this plan?', 'Road lots', 'Road lot', () =>
                this.purposeIsOneOf(DigitalPlanComponent.ACQUISITION_LOT_PURPOSES)
            ),
            this.lotDeclaration(
                'roadWideningLots',
                'Any road widening (as a lot) created on this plan?',
                'Road widening lots',
                'Road widening lot',
                () => this.purposeIsOneOf(DigitalPlanComponent.ACQUISITION_LOT_PURPOSES),
                () => this.roadWideningConflictMessage()
            )
        ];

        // When a save clears a now-stale validation, drop the cached validation so it is no longer displayed.
        this.validationClearedSubscription = this.eventBus.on('planLodgementService:digital-plan-validation-cleared').subscribe(() => {
            this.validationState.clear();
        });

        this.saveSubscription = this.eventBus.on('SAVE_DIGITAL_PLAN').subscribe(() => {
            if (!this.readonly) {
                this.save();
            }
        });
    }

    ngDoCheck() {
        const planModel = this.getPlanModel();
        if (planModel.pdfCreationMethod !== PDF_CREATION_METHOD.DIGITAL_PLAN) {
            return;
        }

        // If this.metro is undefined, derive its value from the digital plan attachment's CSD attributes.
        const csdAttributes = this.getDigitalPlanAttachment()?.csdAttributes;
        if (csdAttributes && this.metro === undefined) {
            const digitalPlanAttachmentCsdAttributesLookup = this.digitalPlanAttachmentCsdAttributesLookups.find(
                l =>
                    l.ssa === (planModel.ssa === 'true') &&
                    l.pnthoracc === csdAttributes.pnthoracc &&
                    l.angacc === csdAttributes.angacc &&
                    l.distacc === csdAttributes.distacc
            );
            if (digitalPlanAttachmentCsdAttributesLookup) {
                this.metro = digitalPlanAttachmentCsdAttributesLookup.metro;
            }
        }

        // Set the digital plan attachment's CSD attributes from the matching (ssa, metro) lookup once metro is defined.
        // Non-statutory plans require an explicit SSA selection; statutory plans (no survey details / SSA not shown)
        // are treated as SSA=FALSE, so the attributes resolve from the FALSE rows of the lookup against the metro region.
        if (!this.readonly && this.metro !== undefined && (this.ssaOptionSelected() || !this.surveyDetailsApplicable())) {
            this.digitalPlanAttachmentCsdAttributesLookup = this.digitalPlanAttachmentCsdAttributesLookups.find(
                l => l.ssa === (planModel.ssa === 'true') && l.metro === this.metro
            );

            const digitalPlanAttachment = this.getDigitalPlanAttachment();
            if (digitalPlanAttachment) {
                if (digitalPlanAttachment.csdAttributes) {
                    if (digitalPlanAttachment.csdAttributes.pnthoracc !== this.digitalPlanAttachmentCsdAttributesLookup.pnthoracc) {
                        digitalPlanAttachment.csdAttributes.pnthoracc = this.digitalPlanAttachmentCsdAttributesLookup.pnthoracc;
                    }
                    if (digitalPlanAttachment.csdAttributes.distacc !== this.digitalPlanAttachmentCsdAttributesLookup.distacc) {
                        digitalPlanAttachment.csdAttributes.distacc = this.digitalPlanAttachmentCsdAttributesLookup.distacc;
                    }
                    if (digitalPlanAttachment.csdAttributes.angacc !== this.digitalPlanAttachmentCsdAttributesLookup.angacc) {
                        digitalPlanAttachment.csdAttributes.angacc = this.digitalPlanAttachmentCsdAttributesLookup.angacc;
                    }
                } else {
                    digitalPlanAttachment.csdAttributes = {
                        pnthoracc: this.digitalPlanAttachmentCsdAttributesLookup.pnthoracc,
                        distacc: this.digitalPlanAttachmentCsdAttributesLookup.distacc,
                        angacc: this.digitalPlanAttachmentCsdAttributesLookup.angacc
                    };
                }
            }
        }

        const planLodgementId = planModel.id;
        if (planLodgementId && planLodgementId !== this.planLodgementIdAtLastCheck) {
            this.planLodgementIdAtLastCheck = planLodgementId;

            // Load any existing validation for the newly loaded plan. This also stops any poll still running for the
            // previously loaded plan; without it, an in-progress poll from another plan keeps emitting and overwrites
            // this plan's validation status (e.g. flipping a completed plan back to "in progress").
            this.validationState.load();
        }

        // Set plan model's planLots and chargeablePlanLots from the current validation (single source of truth).
        if (!this.readonly) {
            this.validationState.applyLotCounts(planModel);
        }

        this.syncLotDeclarations(planLodgementId);
        this.syncVestingLotsDeclaration();
    }

    /** Every lot declaration on the tab, in the order the client asked for them. */
    get lotDeclarations(): LotDeclaration[] {
        return [this.commonPropertyDeclaration, ...this.acquisitionDeclarations];
    }

    /**
     * Seeds each declaration from what was saved, and withdraws any the plan can no longer hold, so that a question the
     * surveyor can no longer see cannot keep an answer that is no longer true.
     */
    private syncLotDeclarations(planLodgementId: number) {
        const digitalPlanAttachment = this.getDigitalPlanAttachment();

        this.lotDeclarations.forEach(declaration => {
            if (!digitalPlanAttachment || !declaration.applicable()) {
                declaration.withdraw();
            } else {
                declaration.seed(planLodgementId);
            }
        });
    }

    /**
     * Keeps the vesting lots question in step with its table. The answer is held on the attachment rather than derived from
     * the table, because a fresh Yes creates no rows to derive it back from until the surveyor fills one in, and because an
     * empty table would otherwise be indistinguishable from a question nobody has answered.
     */
    private syncVestingLotsDeclaration() {
        const digitalPlanAttachment = this.getDigitalPlanAttachment();
        if (this.readonly || !digitalPlanAttachment) {
            return;
        }

        if (!this.vestingLotsApplicable()) {
            if (digitalPlanAttachment.hasVestingLots !== undefined) {
                digitalPlanAttachment.hasVestingLots = undefined;
            }
            if (this.getDigitalPlanAttachmentVestingLots().length) {
                digitalPlanAttachment.vestingLots.length = 0;
            }
            return;
        }

        // Plans that declared vesting lots before the question was recorded have rows but no answer against them.
        if (digitalPlanAttachment.hasVestingLots === undefined && this.getDigitalPlanAttachmentVestingLots().length) {
            digitalPlanAttachment.hasVestingLots = true;
        }
    }

    ngOnDestroy() {
        // Guarantee the validation poll stops when the view is torn down; otherwise it keeps polling in the background
        this.validationState.stopPolling();
        this.uploadDwgSubscription?.unsubscribe();
        this.validationClearedSubscription?.unsubscribe();
        this.saveSubscription?.unsubscribe();
    }

    get readonly() {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.permissions);
    }

    getDigitalPlanAttachment() {
        return this.getPlanModel().digitalPlanAttachment;
    }

    getDigitalPlanAttachmentVestingLots() {
        const digitalPlanAttachment = this.getDigitalPlanAttachment();
        return digitalPlanAttachment?.vestingLots || [];
    }

    onChangeDwg(file: File) {
        this.dwgFile = file;
    }

    onChangeHasVestingLots(hasVestingLots: boolean) {
        const digitalPlanAttachment = this.getDigitalPlanAttachment();
        if (!digitalPlanAttachment) {
            return;
        }

        digitalPlanAttachment.hasVestingLots = hasVestingLots;
        digitalPlanAttachment.vestingLots = digitalPlanAttachment.vestingLots || [];

        if (hasVestingLots) {
            digitalPlanAttachment.vestingLots.splice(0, digitalPlanAttachment.vestingLots.length, {});
        } else {
            digitalPlanAttachment.vestingLots.length = 0;
        }
    }

    onChangeVestingLotPurpose(vestingLot: DigitalPlanAttachmentVestingLot) {
        const vestingPurposeLookup = this.vestingPurposeLookups.find(l => l.code === vestingLot.purpose);
        if (vestingPurposeLookup) {
            vestingLot.description = vestingPurposeLookup.description;
            vestingLot.statutoryReference = vestingPurposeLookup.statutoryReference;
        }
    }

    addVestingLot(index: number) {
        const vestingLots = this.getDigitalPlanAttachmentVestingLots();
        vestingLots.splice(index + 1, 0, {});
    }

    removeVestingLot(index: number) {
        const vestingLots = this.getDigitalPlanAttachmentVestingLots();
        vestingLots.splice(index, 1);
    }

    private lotDeclaration(
        field: DigitalPlanAttachmentLotNumbersField,
        question: string,
        fieldLabel: string,
        lotNoun: string,
        applicable: () => boolean,
        conflict: () => string = () => ''
    ): LotDeclaration {
        return new LotDeclaration(
            field,
            question,
            fieldLabel,
            lotNoun,
            applicable,
            () => this.formerTenureNewLots(),
            conflict,
            () => this.getDigitalPlanAttachment(),
            () => !this.readonly,
            this.tenureService
        );
    }

    private purposeIsOneOf(purposes: string[]): boolean {
        return purposes.includes(this.getPlanModel().planPurpose?.id);
    }

    vestingLotsApplicable(): boolean {
        return this.purposeIsOneOf(DigitalPlanComponent.VESTING_LOT_PURPOSES);
    }

    /**
     * The answer to show against the vesting lots question. Rows stand in for a missing answer, because a plan that declared
     * vesting lots before the answer was recorded has rows and no answer, and a plan nobody can edit is never given one.
     */
    hasVestingLots(): boolean {
        const digitalPlanAttachment = this.getDigitalPlanAttachment();
        return digitalPlanAttachment?.hasVestingLots ?? (this.getDigitalPlanAttachmentVestingLots().length ? true : undefined);
    }

    /**
     * A lot has one acquisition transfer action, and the transfer to the Crown is one a freehold lot alone can carry, so a lot
     * transferred to the Crown can be neither acquired for a public purpose nor a road or road widening lot. Reported against
     * the Crown declaration because it is that answer the others exclude, whichever was entered first.
     */
    private crownTransferConflictMessage(): string {
        const attachment = this.getDigitalPlanAttachment();
        const crownLots = attachment?.lotsTransferredToCrown ?? [];
        const alsoAcquired = crownLots.filter(lot => attachment?.lotsAcquiredForPublicPurpose?.includes(lot));
        const alsoRoad = crownLots.filter(lot => attachment?.roadLots?.includes(lot));
        const alsoRoadWidening = crownLots.filter(lot => attachment?.roadWideningLots?.includes(lot));

        return [
            alsoAcquired.length ? `Lot ${alsoAcquired.join(', ')} cannot be both transferred to the Crown and acquired for a public purpose.` : '',
            alsoRoad.length ? `Lot ${alsoRoad.join(', ')} cannot be both transferred to the Crown and a road lot.` : '',
            alsoRoadWidening.length ? `Lot ${alsoRoadWidening.join(', ')} cannot be both transferred to the Crown and a road widening lot.` : ''
        ]
            .filter(message => message)
            .join(' ');
    }

    /**
     * A lot is either a road or a road widening, never both, the two being separate parcel types. Reported against the road
     * widening declaration so the conflict is stated once, whichever of the two was entered first.
     */
    private roadWideningConflictMessage(): string {
        const attachment = this.getDigitalPlanAttachment();
        const alsoRoad = (attachment?.roadWideningLots ?? []).filter(lot => attachment?.roadLots?.includes(lot));

        return alsoRoad.length ? `Lot ${alsoRoad.join(', ')} cannot be both a road lot and a road widening lot.` : '';
    }

    /**
     * Every lot number the plan creates, as recorded against former tenure, whether against a tenure or one of its lots.
     * Something fully superseded by road creates no numbered lot, so any new lots left recorded against it are skipped, the
     * same way the plan heading and the new lot count skip them.
     */
    private formerTenureNewLots(): Set<number> {
        const newLots = new Set<number>();
        const addNewLots = (newLotDetails: any) => {
            if (!newLotDetails?.fullySupersededByRoad) {
                (newLotDetails?.newLots ?? []).forEach((newLot: number) => newLots.add(newLot));
            }
        };
        (this.getPlanModel().formerTenures ?? []).forEach(ft => {
            addNewLots(ft.newLotDetails);
            (ft.lots ?? []).forEach((lot: any) => addNewLots(lot.newLotDetails));
        });
        return newLots;
    }

    maxFileSizeExceeded() {
        return this.dwgFile && this.dwgFile.size > DigitalPlanComponent.MAX_FILE_SIZE;
    }

    fileExtensionInvalid() {
        return this.dwgFile && !this.dwgFile.name.toLowerCase().endsWith('.dwg');
    }

    confirmClearDwg() {
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: 'You are about to remove the digital plan attachment. Are you sure you wish to continue?'
            })
            .subscribe(action => {
                if (action === 'yes') {
                    this.clearDwg();
                }
            });
    }

    uploadDwgDisabled() {
        return this.readonly || !this.dwgFile || this.maxFileSizeExceeded() || this.fileExtensionInvalid() || this.form?.invalid;
    }

    uploadDwg() {
        this.uploadDwgErrorMessage = '';

        this.uploadDwgSubscription = this.digitalPlanService
            .uploadDwg(this.dwgFile)
            .pipe(timeout(DigitalPlanComponent.UPLOAD_TIMEOUT))
            .subscribe({
                next: (response: DigitalPlanAttachmentVirusScanStatusResponse) => {
                    const fileLocation = response.fileLocation;
                    if (response.status === 'Clean') {
                        console.debug(`uploadDwg - ${fileLocation} - virus scan status: Clean`);
                        const planModel = this.getPlanModel();
                        if (!planModel.digitalPlanAttachment) {
                            planModel.digitalPlanAttachment = {meridian: false};
                        }
                        planModel.digitalPlanAttachment.fileName = this.dwgFile.name;
                        planModel.digitalPlanAttachment.fileLocation = fileLocation;
                        planModel.digitalPlanAttachment.fileSize = this.dwgFile.size;
                    } else if (response.status === 'Infected') {
                        console.error(`uploadDwg - ${fileLocation} - virus scan status: Infected`);
                        this.uploadDwgErrorMessage = DigitalPlanComponent.UPLOAD_FILE_INFECTED_ERROR_MESSAGE;
                    } else if (response.status === 'Timeout') {
                        console.error(`uploadDwg - ${fileLocation} - virus scan status: Timeout`);
                        this.uploadDwgErrorMessage = DigitalPlanComponent.UPLOAD_TIMEOUT_ERROR_MESSAGE;
                    } else {
                        console.error(`uploadDwg - ${fileLocation} - virus scan status: ${response.status}`);
                        this.uploadDwgErrorMessage = DigitalPlanComponent.UPLOAD_ERROR_MESSAGE;
                    }
                },
                error: err => {
                    console.error(`${this.title} - uploadDwg - error:`, err);
                    this.uploadDwgErrorMessage =
                        err instanceof TimeoutError ? DigitalPlanComponent.UPLOAD_TIMEOUT_ERROR_MESSAGE : DigitalPlanComponent.UPLOAD_ERROR_MESSAGE;
                }
            });
        this.eventBus.cast('nlr:busy', this.uploadDwgSubscription);
    }

    cancel() {
        console.debug(`${this.title} - cancel`);

        const subscription = this.cancelValidation().subscribe();
        this.eventBus.cast('nlr:busy', subscription);
    }

    confirmCancel() {
        console.debug(`${this.title} - cancel`);

        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: 'This action will terminate all ongoing validations. Are you sure you wish to continue?'
            })
            .subscribe(action => {
                if (action === 'yes') {
                    this.cancel();
                }
            });
    }

    save() {
        console.debug(`${this.title} - save`);
        this.eventBus.cast('nlr:busy', this.planLodgementService.save().subscribe());
    }

    saveAndValidate() {
        console.debug(`${this.title} - saveAndValidate`);

        // Show busy spinner only during save and cancel of existing validation
        const subscription = this.planLodgementService
            .save({showAlertOnSave: false})
            .pipe(switchMap(() => this.cancelValidation()))
            .subscribe(() => this.validationState.validate());

        this.eventBus.cast('nlr:busy', subscription);
    }

    viewReport(name: string, url: string) {
        const plainTextReports = ['CSD', 'Closure And Area Checking'];
        this.view(name, url, plainTextReports.includes(name) ? 'text/plain;charset=utf-8' : 'text/html');
    }

    saveAndValidateDisabled() {
        return (
            this.readonly ||
            this.form?.invalid ||
            ((this.isAffectedTenure() || this.isFormerTenure()) && (this.tenureCount() === 0 || this.newLotCount() < this.minLotCount())) ||
            (this.surveyDetailsApplicable() && (!this.ssaOptionSelected() || !this.surveyMethodSelected())) ||
            !this.projectedCoordinateSystemSelected() ||
            this.lotDeclarationsIncomplete() ||
            this.digitalPlanService.isValidationInProgress(this.validationState.validation)
        );
    }

    /**
     * Whether any declaration the plan is asked for is unanswered, or answered Yes without naming the lots it applies to, or
     * names lots the plan cannot hold. Only the questions actually asked count, so a declaration withdrawn along with a
     * purpose change cannot block a plan that no longer shows it.
     */
    private lotDeclarationsIncomplete(): boolean {
        return this.lotDeclarations
            .filter(declaration => declaration.applicable())
            .some(declaration => declaration.unanswered() || declaration.missing() || declaration.textInvalid() || !!declaration.error());
    }

    validationInProgress() {
        return this.digitalPlanService.isValidationInProgress(this.validationState.validation);
    }

    getValidationResults() {
        return this.validationState.validation?.results?.length ? this.validationState.validation.results : this.defaultValidationResults;
    }

    get defaultValidationResults(): DigitalPlanValidationStatusResponseResult[] {
        const results: DigitalPlanValidationStatusResponseResult[] = [
            {type: 'Base', status: 'Not Started'},
            {type: 'Generate Plan PDF', status: 'Not Started'},
            {type: 'CAD Standard', status: 'Not Started'}
        ];

        if (!this.isStatutoryPlan()) {
            results.push(
                {type: 'Extract Details', status: 'Not Started'},
                {type: 'Plan Presentation Guidelines', status: 'Not Started'},
                {type: 'CSD', status: 'Not Started'},
                {type: 'Closure and Area Checking', status: 'Not Started'}
            );
        }

        return results;
    }

    isStatutoryPlan() {
        const id = this.getPlanModel().planPurpose?.id;
        return id === 'FH_STAT' || id === 'CR_STAT';
    }

    tenureCount() {
        return this.getPlanModel().formerTenures?.length || 0;
    }

    isAffectedTenure() {
        return this.getPlanModel().planPurpose?.parentTenureAssociationType === 'AFFECTED_TENURE';
    }

    isFormerTenure() {
        return this.getPlanModel().planPurpose?.parentTenureAssociationType === 'FORMER_TENURE';
    }

    newLotCount() {
        let count = 0;
        (this.getPlanModel().formerTenures ?? []).forEach(ft => {
            if (ft.newLotDetails?.fullySupersededByRoad) {
                count++;
            } else {
                count += (ft.newLotDetails?.newLots ?? []).length;
            }
            (ft.lots ?? []).forEach((lot: any) => {
                if (lot.newLotDetails?.fullySupersededByRoad) {
                    count++;
                } else {
                    count += (lot.newLotDetails?.newLots ?? []).length;
                }
            });
        });
        return count;
    }

    minLotCount() {
        const minNumberLotsCreated = this.getPlanModel().planPurpose?.minNumberLotsCreated;
        return isNaN(minNumberLotsCreated) ? 0 : minNumberLotsCreated;
    }

    surveyDetailsApplicable() {
        return this.uiConfigurationService.getInfoUiGroup()?.surveyDetailsDisplay;
    }

    ssaOptionSelected() {
        return !!this.getPlanModel().ssa;
    }

    surveyMethodSelected() {
        return !!this.getPlanModel().surveyMethod;
    }

    projectedCoordinateSystemSelected() {
        return !!this.getPlanModel().spatialSettings?.projectedCoordSystem;
    }

    supportsMeridians() {
        return this.getPlanModel().planPurpose?.supportsMeridians ?? false;
    }

    private view(name: string, url: string, type: string) {
        this.http.get(url, {responseType: 'arraybuffer'}).subscribe((data: ArrayBuffer) => {
            const file = new Blob([data], {type: type});
            const fileURL = URL.createObjectURL(file);
            const popup = window.open(fileURL, '_blank', `height=${screen.height},width=${screen.width}`);
            popup.addEventListener('load', () => {
                popup.document.title = name;
            });
            popup.moveTo(0, 0);
            popup.onload = () => {
                URL.revokeObjectURL(fileURL);
            };
        });
    }

    private clearDwg() {
        if (this.dwgFileInput?.nativeElement?.value) {
            this.dwgFileInput.nativeElement.value = null;
        }

        if (this.dwgFile) {
            delete this.dwgFile;
        }

        if (this.getPlanModel().digitalPlanAttachment) {
            delete this.getPlanModel().digitalPlanAttachment;
        }

        this.metro = undefined;

        const subscription = this.cancelValidation().subscribe(() => {
            this.notificationAlertsService.addMessage({type: 'success', text: 'The digital plan attachment has been removed successfully'});
        });

        this.eventBus.cast('nlr:busy', subscription);
    }

    private cancelValidation() {
        console.debug(`${this.title} - cancelValidation`);

        this.validationState.stopPolling();

        const validation = this.validationState.validation;
        if (validation) {
            return this.digitalPlanService.cancelValidation(validation.correlationId).pipe(
                tap(() => this.validationState.clear()),
                catchError((error: HttpErrorResponse) => {
                    console.error(`${this.title} - cancelValidation - error:`, error);
                    this.alertModalService.createAlertMessage(error.error);
                    return EMPTY;
                })
            );
        }

        return of(null);
    }

    private getPlanModel() {
        return this.planModelService.getModel();
    }
}
